import { IPlatformSupportVO } from 'tag/ts/smart_tag/interfaces/IPlatformSupportVO';
import { ISmartTagReadyCallback } from 'tag/ts/smart_tag/interfaces/ISmartTagReadyCallback';
import { TBrowserConfig } from 'tag/ts/smart_tag/types/TBrowserConfig';
import { IPlatformSupportData } from 'tag/ts/tag/interfaces/utilities/platform_support/IPlatformSupportData';
import { IPlatformSupportReject } from 'tag/ts/tag/interfaces/utilities/platform_support/IPlatformSupportReject';
import { IPlatformSupportResolve } from 'tag/ts/tag/interfaces/utilities/platform_support/IPlatformSupportResolve';

/**
 * @description Returns platform support flag for HTML5 video
 * @param {ISmartTagReadyCallback} readyCallback
 * @return {Promise<IPlatformSupportData>}
 */
export const platformSupported = ( readyCallback: ISmartTagReadyCallback ): Promise<IPlatformSupportData> => {

	/**
	 * @description Returns whether current user's OS and platform is supported
	 * @return {boolean}
	 */
	const isSupported = (): boolean => {
		if ( !readyCallback || !readyCallback.platformConfig ) return true; // attempt to support if platform config settings are unavailable

		const capabilities = window?.SmartTag?.capabilities;
		const DEFAULT = 'DEFAULT';

		// mobile use case => allow all browsers https://jira.cbsi.com/browse/CBSVIDEO-5760
		if ( capabilities?.isMobile?.any() === true ) {
			return true;
		}

		const currentOS = (
			(): string => {
				if ( capabilities?.os !== null ) {
					return capabilities?.os;
				} else {
					return DEFAULT;
				}
			}
		)();

		const currentBrowser = capabilities.browser;
		const currentBrowserVersion = capabilities.browserVersion;

		const osConfig: TBrowserConfig = readyCallback.platformConfig[ currentOS ] || readyCallback.platformConfig.DEFAULT;
		if ( osConfig.disable ) return false; // client disabled current OS

		const browserConfig: IPlatformSupportVO = osConfig[ currentBrowser ] || readyCallback.platformConfig.DEFAULT[ currentBrowser ];

		if ( !browserConfig ) return true; // attempt to support

		if ( browserConfig.disable ) return false; // client disabled current browser

		// check if browser version is supported
		if ( browserConfig.minVersion && browserConfig.maxVersion ) return browserConfig.minVersion <= currentBrowserVersion && browserConfig.maxVersion >= currentBrowserVersion;
		else if ( browserConfig.minVersion ) return browserConfig.minVersion <= currentBrowserVersion;
		else if ( browserConfig.maxVersion ) return browserConfig.maxVersion >= currentBrowserVersion;

		return true; // attempt to support
	};

	return new Promise(
		( resolve: IPlatformSupportResolve, reject: IPlatformSupportReject ) => {
			// resolve - reject
			isSupported() ? resolve( { supported: true } ) : reject( { supported: false } );
		}
	);
};